import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import {
  Target, Rocket, BookOpen, Award, Users, Building, ShieldCheck,
  HelpCircle, ChevronDown, CheckCircle2, Star, ArrowRight,
  Heart, Sparkles, GraduationCap
} from 'lucide-react';
import StructuredData from '../components/StructuredData';
import '../styles/AboutUs_page.scss';

export default function AboutUs() {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const navItems = [
    { id: 'about-school', label: t('nav_about_school') },
    { id: 'principal-desk', label: t('nav_principal_desk') },
    { id: 'affiliation', label: t('nav_affiliation') },
    { id: 'facilities', label: t('nav_facilities') },
    { id: 'mandatory-disclosure', label: t('nav_mandatory_disclosure') },
    { id: 'faqs', label: t('nav_faqs') },
    { id: 'vision-mission', label: t('nav_vision_mission') },
    { id: 'history-achievements', label: t('nav_history') },
    { id: 'management-committee', label: t('nav_management_committee') },
  ];

  const facilitiesList = [
    {
      title: 'Smart & Digital Classrooms',
      desc: 'Spacious, well-ventilated classrooms with multimedia learning tools that bring concepts to life.',
      icon: <Sparkles className="fac-icon" size={26} />,
      badge: 'Interactive'
    },
    {
      title: 'Modern Computer Laboratory',
      desc: 'Fully equipped IT lab with modern computers, high-speed internet, and regular practical coding sessions.',
      icon: <Building className="fac-icon" size={26} />,
      badge: 'Digital Skills'
    },
    {
      title: 'Composite Science Laboratory',
      desc: 'Hands-on laboratory for Physics, Chemistry, and Biology experiments fostering scientific curiosity.',
      icon: <Star className="fac-icon" size={26} />,
      badge: 'Experiential'
    },
    {
      title: 'Rich Knowledge Library',
      desc: 'Curated collection of academic books, storybooks, reference materials, newspapers, and educational magazines.',
      icon: <BookOpen className="fac-icon" size={26} />,
      badge: '3,500+ Books'
    },
    {
      title: 'Spacious Sports Playground',
      desc: 'Dedicated sports arena for Cricket, Football, Volleyball, Badminton, Athletics, and physical education drills.',
      icon: <Award className="fac-icon" size={26} />,
      badge: 'Fitness'
    },
    {
      title: 'Safe Fleet Transportation',
      desc: 'GPS-enabled school buses and vans with trained drivers and attendants covering major surrounding routes.',
      icon: <ShieldCheck className="fac-icon" size={26} />,
      badge: 'Safe & Reliable'
    },
    {
      title: 'Creative Arts & Dance Studio',
      desc: 'Dedicated creative space for classical & folk dance, painting, music, and performing arts.',
      icon: <Heart className="fac-icon" size={26} />,
      badge: 'Creativity'
    },
    {
      title: 'Safe RO Drinking Water & Clean Sanitation',
      desc: 'Multi-stage RO purification plants and regularly sanitized hygienic separate washrooms for boys and girls.',
      icon: <CheckCircle2 className="fac-icon" size={26} />,
      badge: 'Health First'
    }
  ];

  const faqs = [
    {
      q: 'When was Prabhat Tara Vidya Niketan established and what board is it affiliated with?',
      a: 'Prabhat Tara Vidya Niketan was established in 1995 and has been serving students for 30+ years. The school is registered with the Bihar Government and follows the CBSE curriculum standards up to Secondary Level (Class 10).'
    },
    {
      q: 'What classes are conducted at the school?',
      a: 'We offer education from Pre-Primary (Nursery, LKG, UKG) through Primary (Classes 1-5), Middle School (Classes 6-8), to Secondary School (Classes 9-10).'
    },
    {
      q: 'What is the medium of instruction at Prabhat Tara Vidya Niketan?',
      a: 'The primary medium of instruction is English, with strong emphasis on bilingual proficiency in Hindi and Sanskrit as prescribed by curriculum guidelines.'
    },
    {
      q: 'Does the school provide transportation facilities?',
      a: 'Yes, the school operates a well-managed transport network covering Rajkhand, Goraul, and surrounding rural and semi-urban localities within a 10 km radius.'
    },
    {
      q: 'What are the school office working hours for parent enquiries?',
      a: 'The administrative office operates Monday to Saturday from 8:00 AM to 3:30 PM. Parents can meet the Principal with prior appointment between 10:00 AM and 12:30 PM.'
    },
    {
      q: 'How does the school support students in co-curricular growth?',
      a: 'We provide structured classes in Sports, Computer Coding, Dance, Public Speaking, Fine Arts, and Science Exhibitions alongside academics to ensure balanced character development.'
    }
  ];

  const milestones = [
    { year: '1995', title: 'Foundation of PTVN', desc: 'Established with a vision to provide world-class affordable education to the children of Goraul and Vaishali.' },
    { year: '2005', title: 'Campus Expansion & Modern Labs', desc: 'Introduced state-of-the-art Science and Computer labs and inaugurated the dedicated playground.' },
    { year: '2015', title: 'Digital Classrooms & Coding', desc: 'Implemented digital smart class modules and early computer programming education for young learners.' },
    { year: '2020', title: 'Excellence in Board Examinations', desc: 'Achieved outstanding 100% board examination pass record with top district ranks.' },
    { year: 'Present', title: '30+ Years of Educational Legacy', desc: 'Proudly educating 1,200+ students with 50+ dedicated faculty members, shaping tomorrow’s leaders.' },
  ];

  const managementMembers = [
    { name: 'Mr. Shubh Nath Singh', role: 'Director & Founder Patron', desc: 'Visionary educationist spearheading institutional growth, infrastructure, and core academic policies for over 3 decades.' },
    { name: 'Mr. Kishan', role: 'Principal & Academic Head', desc: 'Experienced educator providing pedagogical leadership, teacher mentoring, and student-centered curriculum execution.' },
    { name: 'Senior Management Council', role: 'Governing Advisory Board', desc: 'Comprising distinguished educationists, local community guardians, and teacher representatives ensuring transparent administration.' },
    { name: 'Parents-Teachers Council', role: 'Collaborative Committee', desc: 'Active platform fostering constructive dialogue between parents and faculty for continuous student development.' }
  ];

  return (
    <div className="about-page">
      <StructuredData type="organization" />
      <Helmet>
        <title>About Us - Prabhat Tara Vidya Niketan Goraul</title>
        <meta
          name="description"
          content="Learn about Prabhat Tara Vidya Niketan - 30+ years of educational excellence in Goraul, Vaishali. Vision, Mission, Affiliation, Facilities, Principal message, and Management."
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/about-us" />
      </Helmet>

      {/* Hero Banner */}
      <div className="hero-banner">
        <div className="hero-inner">
          <span className="badge">
            <Award size={16} /> 30+ Years of Educational Legacy (Since 1995)
          </span>
          <h1>About Prabhat Tara Vidya Niketan</h1>
          <p>
            Nurturing young intellects with knowledge, character, discipline, and modern innovation in Rajkhand, Goraul, Vaishali.
          </p>
        </div>
      </div>

      {/* Sticky Nav Pills */}
      <div className="nav-pills-bar">
        <div className="nav-container">
          {navItems.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <div className="about-content-wrapper">

        {/* 1. ABOUT THE SCHOOL */}
        <section id="about-school" className="about-card">
          <span className="card-tag">1. About the School</span>
          <h2>A Sanctuary for Intellectual & Moral Awakening</h2>

          <div className="about-school-grid">
            <div className="story-copy">
              <p>
                Prabhat Tara Vidya Niketan (PTVN), located in Rajkhand, Goraul, Vaishali (Bihar), was founded in 1995 with the steadfast belief that quality, value-driven education should be accessible to all students. Over the course of three decades, PTVN has evolved into a premier educational institution recognized across the district.
              </p>
              <p>
                Our educational philosophy blends rigorous academic curricula with vibrant co-curricular disciplines such as sports, computer coding, dance, and fine arts. We nurture each student's intrinsic talents, instilling self-discipline, curiosity, empathy, and social responsibility.
              </p>
              <div className="stats-strip">
                <div className="stat-item">
                  <h4 style={{ color: '#011673' }}>30+</h4>
                  <p>Years of Service</p>
                </div>
                <div className="stat-item">
                  <h4 style={{ color: '#27ae60' }}>1200+</h4>
                  <p>Active Students</p>
                </div>
                <div className="stat-item">
                  <h4 style={{ color: '#ff6b2b' }}>50+</h4>
                  <p>Expert Faculty</p>
                </div>
              </div>
            </div>
            <div className="campus-image-wrap">
              <img
                src="/descriptive-image/school-building-front-view.jpg"
                alt="Prabhat Tara Vidya Niketan School Campus"
                loading="lazy"
                onError={(e) => { e.currentTarget.src = '/images/building.jpg'; }}
              />
            </div>
          </div>
        </section>

        {/* 2. PRINCIPAL'S DESK */}
        <section id="principal-desk" className="about-card">
          <span className="card-tag">2. Principal's Desk</span>
          <h2>"Guiding Minds, Inspiring Futures"</h2>

          <div className="principal-desk-grid">
            <div className="principal-portrait">
              <img
                src="/images/principal.png"
                alt="Principal Mr. Kishan"
                loading="lazy"
              />
              <h3>Mr. Kishan</h3>
              <p className="role-text">Principal, PTVN</p>
              <p className="cred-text">M.A., B.Ed. | 20+ Years in Educational Leadership</p>
            </div>
            <div className="principal-copy">
              <blockquote>
                "At Prabhat Tara Vidya Niketan, education transcends conventional textbooks. It is a transformational journey where traditional values harmonize with modern technological tools. We empower each student to think critically, act ethically, and pursue excellence with unwavering conviction."
              </blockquote>
              <p>
                I invite all parents to partner with us as co-architects of our children’s destinies. Together, we cultivate an ecosystem where questions are celebrated, character is honed, and dreams are transformed into tangible achievements.
              </p>
              <div className="cta-row">
                <Link to="/principal-message" className="btn-desk-primary">
                  <span>Read Full Principal's Message</span>
                  <ArrowRight size={16} />
                </Link>
                <Link to="/director-message" className="btn-desk-secondary">
                  <span>Director's Address</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 3. AFFILIATION DETAILS */}
        <section id="affiliation" className="about-card">
          <span className="card-tag">3. Affiliation Details</span>
          <h2>Board Affiliation & Academic Recognition</h2>
          <div className="affiliation-grid">
            <div className="affil-box">
              <div className="icon-wrap" style={{ color: '#011673' }}>
                <ShieldCheck size={28} />
              </div>
              <h3>Curriculum Standard</h3>
              <p>
                Strictly adheres to the standardized <strong>CBSE Curriculum Framework</strong> (NCERT-based syllabus) with continuous comprehensive evaluation and national educational standards.
              </p>
            </div>
            <div className="affil-box">
              <div className="icon-wrap" style={{ color: '#27ae60' }}>
                <Award size={28} />
              </div>
              <h3>Government Recognition</h3>
              <p>
                Fully recognized and registered under the <strong>Department of Education, Government of Bihar</strong>, complying with all RTE norms and safety protocols.
              </p>
            </div>
            <div className="affil-box">
              <div className="icon-wrap" style={{ color: '#ff6b2b' }}>
                <GraduationCap size={28} />
              </div>
              <h3>Recognized Levels</h3>
              <p>
                Authorized to impart quality education from <strong>Playgroup & Nursery up to Class 10 (Secondary Level)</strong> with certified transfer certificates valid across India.
              </p>
            </div>
          </div>
        </section>

        {/* 4. OUR FACILITIES */}
        <section id="facilities" className="about-card">
          <span className="card-tag">4. Campus Infrastructure</span>
          <h2>Our World-Class Facilities</h2>
          <p className="card-desc">
            Designed to create an immersive, inspiring, and safe learning environment where every child thrives academically and physically.
          </p>
          <div className="facilities-grid">
            {facilitiesList.map((fac, idx) => (
              <div key={idx} className="facility-card">
                <div className="card-top">
                  {fac.icon}
                  <span className="fac-badge">{fac.badge}</span>
                </div>
                <h3>{fac.title}</h3>
                <p>{fac.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 5. MANDATORY PUBLIC DISCLOSURE */}
        <section id="mandatory-disclosure" className="about-card disclosure-banner-card">
          <span className="card-tag">5. Transparency & Compliance</span>
          <h2>Mandatory Public Disclosure</h2>
          <p className="card-desc">
            We uphold utmost transparency with parents, educational authorities, and regulatory bodies. Our complete CBSE SARAS disclosure includes legal registrations, building and fire safety certificates, school management council lists, academic calendars, and infrastructure parameters.
          </p>
          <div className="disclosure-split">
            <div>
              <Link to="/mandatory-disclosure" className="btn-disclosure">
                <span>Open Mandatory Disclosure Page</span>
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="summary-box">
              <h3>Disclosed Sections Summary</h3>
              <ul>
                {[
                  'Section A: School General Information & Codes',
                  'Section B: NOC, Building & Fire Safety Certificates',
                  'Section C: Academic Calendar & 100% Board Results',
                  'Section D: Qualified Teaching Faculty Details (50+ Staff)',
                  'Section E: Infrastructure, Laboratories & Playground Area'
                ].map((item, i) => (
                  <li key={i}>
                    <CheckCircle2 size={16} className="check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 6. FREQUENTLY ASKED QUESTIONS (FAQS) */}
        <section id="faqs" className="about-card">
          <span className="card-tag">6. Got Questions?</span>
          <h2>Frequently Asked Questions (FAQs)</h2>
          <p className="card-desc">
            Everything parents and guardians need to know about admissions, academics, and daily school life.
          </p>
          <div className="faqs-container">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-card ${activeFaq === index ? 'active' : ''}`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="faq-trigger"
                  aria-expanded={activeFaq === index}
                >
                  <span className="faq-title">
                    <HelpCircle size={18} className="faq-icon" /> {faq.q}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`faq-chevron ${activeFaq === index ? 'rotated' : ''}`}
                  />
                </button>
                {activeFaq === index && (
                  <div className="faq-body">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 7. VISION & MISSION */}
        <section id="vision-mission" className="about-card">
          <span className="card-tag">7. Purpose & Direction</span>
          <h2>Our Vision & Mission</h2>
          <p className="card-desc">
            The foundational pillars that guide our institutional ethics, leadership, and teaching methodologies.
          </p>
          <div className="vision-mission-grid">
            <div className="vm-card vision-card">
              <div className="vm-header">
                <div className="vm-icon-bubble" style={{ background: '#ff6b6b20', color: '#ff6b6b' }}>
                  <Target size={30} />
                </div>
                <h3>Our Vision</h3>
              </div>
              <p>{t('vision_content')}</p>
              <div className="vm-tags">
                {['Excellence in Academics', 'Global Mindset', 'Rooted Values', 'Innovation'].map((t, i) => (
                  <span key={i} style={{ background: '#ff6b6b15', color: '#b91c1c' }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="vm-card mission-card">
              <div className="vm-header">
                <div className="vm-icon-bubble" style={{ background: '#4ecdc420', color: '#0d9488' }}>
                  <Rocket size={30} />
                </div>
                <h3>Our Mission</h3>
              </div>
              <p>{t('mission_content')}</p>
              <div className="vm-tags">
                {['Holistic Growth', 'Scientific Temper', 'Ethical Leadership', 'Skill Empowerment'].map((t, i) => (
                  <span key={i} style={{ background: '#4ecdc420', color: '#0f766e' }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 8. OUR HISTORY & ACHIEVEMENTS */}
        <section id="history-achievements" className="about-card">
          <span className="card-tag">8. 30 Years of Service</span>
          <h2>Our Journey & Key Achievements</h2>
          <p className="card-desc">
            From humble beginnings in 1995 to becoming one of Vaishali's most respected educational centers.
          </p>

          <div className="history-grid">
            <div className="timeline">
              {milestones.map((m, idx) => (
                <div key={idx} className="milestone-item">
                  <div className="milestone-dot"></div>
                  <span className="year-badge">{m.year}</span>
                  <h4>{m.title}</h4>
                  <p>{m.desc}</p>
                </div>
              ))}
            </div>
            <div className="topper-banner-wrap">
              <img
                src="/descriptive-image/school-toppers-class-x-congratulations-2.png"
                alt="School Toppers and Achievements"
                loading="lazy"
                onError={(e) => { e.currentTarget.src = '/descriptive-image/students-holding-certificates-drawings-school.jpg'; }}
              />
              <div className="topper-note">
                <strong>🏆 Consistent 100% Board Pass Percentage</strong>
                <span>Proudly producing district toppers, NTSE scholars, and distinguished alumni over 30 years.</span>
              </div>
            </div>
          </div>
        </section>

        {/* 9. MANAGEMENT COMMITTEE */}
        <section id="management-committee" className="about-card">
          <span className="card-tag">9. Governance & Leadership</span>
          <h2>School Managing Committee (SMC)</h2>
          <p className="card-desc">
            Guiding Prabhat Tara Vidya Niketan with visionary integrity, academic excellence, and progressive governance.
          </p>
          <div className="smc-grid">
            {managementMembers.map((member, idx) => (
              <div key={idx} className="smc-card">
                <div className="smc-icon-wrap">
                  <Users size={22} />
                </div>
                <h3>{member.name}</h3>
                <span className="smc-role">{member.role}</span>
                <p>{member.desc}</p>
              </div>
            ))}
          </div>

          <div className="faculty-link-center">
            <Link to="/teaching-staff" className="btn-view-faculty">
              <span>View Our Complete Teaching Faculty Directory</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>

      </div>
    </div>
  );
}
